/* =========================================================================
   WorkspaceTabItem — workspace tab button with 3-dot hover menu & inline editing
   ========================================================================= */

import React, { useState, useRef, useEffect } from 'react';
import { TelemetryDashboardType } from '../lib/dashboard-types';

interface WorkspaceTabItemProps {
  db: TelemetryDashboardType;
  isActive: boolean;
  onSelect: () => void;
  onRename: (id: string, newName: string) => void;
  onContextMenu: (e: React.MouseEvent, startInlineEdit: () => void) => void;
  onMenuClick: (e: React.MouseEvent, startInlineEdit: () => void) => void;
}

export const WorkspaceTabItem: React.FC<WorkspaceTabItemProps> = ({
  db,
  isActive,
  onSelect,
  onRename,
  onContextMenu,
  onMenuClick,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(db.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditName(db.name);
  }, [db.name]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const handleStartEdit = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== db.name) {
      onRename(db.id, trimmed);
    } else {
      setEditName(db.name);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditName(db.name);
      setIsEditing(false);
    }
  };

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onMenuClick(e, () => setIsEditing(true));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditName(e.target.value);
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    onContextMenu(e, () => setIsEditing(true));
  };

  const tabRef = useRef<HTMLButtonElement | HTMLDivElement>(null);

  useEffect(() => {
    if (isActive && tabRef.current) {
      tabRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [isActive]);

  if (isEditing) {
    return (
      <div
        id={`telemetry-tab-item-editing-${db.id}`}
        ref={tabRef as React.RefObject<HTMLDivElement>}
        className={`workspace-tab-btn workspace-tab-btn-editing ${isActive ? 'workspace-tab-btn-active' : ''}`}
      >
        <input
          id={`telemetry-tab-rename-input-${db.id}`}
          ref={inputRef}
          type="text"
          className="workspace-tab-input"
          value={editName}
          onChange={handleInputChange}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          onClick={handleInputClick}
        />
      </div>
    );
  }

  return (
    <button
      id={`telemetry-tab-btn-${db.id}`}
      ref={tabRef as React.RefObject<HTMLButtonElement>}
      className={`workspace-tab-btn ${isActive ? 'workspace-tab-btn-active' : ''}`}
      onClick={onSelect}
      onDoubleClick={handleStartEdit}
      onContextMenu={handleContextMenu}
      title="Hover for options, double-click to rename"
    >
      <span id={`telemetry-tab-label-${db.id}`}>{db.name}</span>
      <span
        id={`telemetry-tab-more-btn-${db.id}`}
        className="workspace-tab-more-icon"
        onClick={handleMoreClick}
        title="Workspace options"
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </span>
    </button>
  );
};

export default WorkspaceTabItem;
